// src/components/EmailPopup.js
// Email capture popup — appears after 10 seconds on first visit.
// Submits email directly to Klaviyo and adds the subscriber to the
// newsletter list. On success, reveals the WELCOME10 discount code.
// Only shows once per browser using localStorage.

import React, { useState, useEffect } from 'react';
import { identifyEmail } from '../lib/analytics';
import { X } from 'lucide-react';

const POPUP_KEY = 'maisondoud_popup_seen';
const DISCOUNT_CODE = 'WELCOME10';
const DELAY_MS = 10000;

const KLAVIYO_PUBLIC_KEY = 'VAf7Tx';
const KLAVIYO_LIST_ID = 'Yz3kiC';

const EmailPopup = () => {
    const [visible, setVisible] = useState(false);
    const [email, setEmail] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (localStorage.getItem(POPUP_KEY)) return;
        const timer = setTimeout(() => setVisible(true), DELAY_MS);
        return () => clearTimeout(timer);
    }, []);

    const dismiss = () => {
        setVisible(false);
        localStorage.setItem(POPUP_KEY, 'true');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email) return;

        setLoading(true);
        setError('');

        try {
            const profileRes = await fetch('https://a.klaviyo.com/client/profiles/?company_id=' + KLAVIYO_PUBLIC_KEY, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'revision': '2023-12-15',
                },
                body: JSON.stringify({
                    data: {
                        type: 'profile',
                        attributes: {
                            email,
                            properties: { source: 'Website Popup' },
                        },
                    },
                }),
            });

            if (!profileRes.ok && profileRes.status !== 409) {
                throw new Error('Failed to create profile');
            }

            await fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + KLAVIYO_PUBLIC_KEY, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'revision': '2023-12-15',
                },
                body: JSON.stringify({
                    data: {
                        type: 'subscription',
                        attributes: {
                            list_id: KLAVIYO_LIST_ID,
                            email,
                            custom_source: 'Website Popup',
                        },
                    },
                }),
            });

            setSubmitted(true);
            localStorage.setItem(POPUP_KEY, 'true');
            identifyEmail(email);

        } catch (err) {
            setError('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (!visible) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
                onClick={dismiss}
            />

            {/* Popup card */}
            <div className="fixed inset-0 z-[101] flex items-center justify-center px-4">
                <div className="relative w-full max-w-md bg-background border border-border rounded-2xl shadow-2xl overflow-hidden" style={{ maxWidth: 'min(448px, calc(100vw - 2rem))' }}>

                    {/* Decorative gold bar */}
                    <div className="h-1 bg-gradient-to-r from-[#CEAC8F]/30 via-[#CEAC8F] to-[#CEAC8F]/30" />

                    {/* Close button */}
                    <button
                        onClick={dismiss}
                        className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors z-10"
                        aria-label="Close"
                    >
                        <X className="w-5 h-5" />
                    </button>

                    <div className="p-8 sm:p-10">
                        {!submitted ? (
                            <>
                                {/* Header */}
                                <div className="text-center mb-8">
                                    <h2 className="text-2xl sm:text-3xl font-light text-foreground tracking-wide mb-3">
                                        Your Signature Starts Here
                                    </h2>
                                    <p className="text-[#CEAC8F] text-lg font-light mb-2">
                                        10% off — on us.
                                    </p>
                                    <p className="text-muted-foreground text-sm leading-relaxed">
                                        Join the MAISON D'OUD list and receive your discount code instantly.
                                        Be first to know about new releases and exclusive drops.
                                    </p>
                                </div>

                                {/* Email form */}
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <input
                                        type="email"
                                        name="email"
                                        value={email}
                                        onChange={(e) => { setEmail(e.target.value); setError(''); }}
                                        placeholder="your@email.com"
                                        required
                                        className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[#CEAC8F]/50 transition-colors"
                                    />

                                    {error && (
                                        <p className="text-red-400 text-xs">{error}</p>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={loading || !email}
                                        className="w-full bg-[#CEAC8F] text-black py-4 rounded-lg font-medium hover:bg-[#d4b896] transition-colors disabled:opacity-60"
                                    >
                                        {loading ? 'Sending...' : 'Claim My 10% Off'}
                                    </button>
                                </form>

                                {/* Skip link */}
                                <p className="text-center mt-4">
                                    <button
                                        onClick={dismiss}
                                        className="text-muted-foreground text-xs hover:text-foreground transition-colors"
                                    >
                                        No thanks, I'll pay full price
                                    </button>
                                </p>
                            </>
                        ) : (
                            /* Success — reveals discount code */
                            <div className="text-center py-4">
                                <h2 className="text-2xl font-light text-foreground tracking-wide mb-3">
                                    Welcome to MAISON D'OUD.
                                </h2>
                                <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
                                    Your discount code is ready. Use it at checkout for 10% off your first order.
                                </p>

                                {/* Discount code */}
                                <div className="bg-[#CEAC8F]/10 border border-[#CEAC8F]/30 rounded-xl px-6 py-4 mb-6">
                                    <p className="text-xs text-muted-foreground mb-1 tracking-widest uppercase">Your code</p>
                                    <p className="text-2xl font-light text-[#CEAC8F] tracking-widest">
                                        {DISCOUNT_CODE}
                                    </p>
                                </div>

                                <p className="text-muted-foreground text-xs mb-6">
                                    Valid on your first order. Single use.
                                </p>

                                <button
                                    onClick={dismiss}
                                    className="w-full bg-[#CEAC8F] text-black py-4 rounded-lg font-medium hover:bg-[#d4b896] transition-colors"
                                >
                                    Start Shopping
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default EmailPopup;